<script lang="ts" setup>
import { ISingleSystemSetupParameter, TaskTypeDescription } from '@/api/generator';
import Dialog from '@/components/Dialog/Dialog.vue';
import Checkbox from '@/components/forms/Checkbox/Checkbox.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import OverviewTableHeader from '@/components/OverviewTable/OverviewTableHeader.vue';
import { translatedEntityNameIncludes } from '@/utils/helper';
import SetupParameterLabel from '@/views/tasks/components/SetupParameterLabel.vue';
import { useTranslation } from 'i18next-vue';
import { cloneDeep } from 'lodash';
import { computed, ref, watch } from 'vue';
import SetupParameterOverviewFilter from './SetupParameterOverviewFilter.vue';
import { SetupParameterOverviewFilterObject } from './types';
import InlineNotification from '@/components/notifications/InlineNotification.vue';

const { t } = useTranslation();

const props = defineProps<{
  modelValue?: boolean;
  parameters: Array<ISingleSystemSetupParameter> | null | undefined;
  taskType: TaskTypeDescription;
}>();

const internalParameters = ref<Array<ISingleSystemSetupParameter>>([]);

watch(
  () => props.modelValue,
  (newModelValue) => {
    if (newModelValue) {
      internalParameters.value = cloneDeep(props.parameters) || [];
    }
  },
);

function handleDialogClose() {
  emits('update:modelValue', false);
}

const emits = defineEmits<{
  addParameters: [parameters: Array<ISingleSystemSetupParameter>];
  'update:modelValue': [boolean];
}>();

function addParameters() {
  emits('addParameters', internalParameters.value);
  emits('update:modelValue', false);
}

const filteredInternalParameters = computed(() => {
  const searchString = filterObject.value.search;

  return internalParameters.value?.filter((p) => translatedEntityNameIncludes(p, searchString));
});

const filterObject = ref<SetupParameterOverviewFilterObject>({});

const headers = [
  {
    title: '',
    key: 'checkbox',
    width: 30,
    sortable: false,
  },
  {
    title: ``,
    key: 'nameAndID',
    width: 500,
    sortable: false,
  },
  {
    title: t('Unit_one'),
    key: 'unit',
    width: 50,
    sortable: false,
  },
] as const;
</script>

<template>
  <Dialog :modelValue="modelValue" size="large" @update:modelValue="handleDialogClose">
    <template #title>
      <div class="d-flex flex-wrap gc-8">
        <span class="flex-1-0-100">{{ $t('AddSetupParameter') }}</span>
        <SetupParameterOverviewFilter v-model="filterObject" class="mb-1" />
        <InlineNotification
          class="flex-0-0 align-self-end mb-3"
          :value="{
            type: 'info',
            message: $t('MissingSetupParametersHint'),
          }"
        />
      </div>
    </template>
    <template #text>
      <v-row style="height: 60vh">
        <v-col>
          <OverviewTable
            :headers="headers"
            :showItemsCount="false"
            data-testid="DataTable_SetupParameter"
            :items="filteredInternalParameters"
            :zebra="true"
            :noSpacing="true"
            @click:row="
              (_event, data) => {
                !data.item.isRequired && (data.item.isEnabled = !data.item.isEnabled);
              }
            "
          >
            <template #header.nameAndID="headerProps">
              <OverviewTableHeader v-bind="headerProps">
                {{ $t('Name') }}
                <span class="font-weight-medium">| {{ $t('ID') }}</span>
              </OverviewTableHeader>
            </template>
            <template #item.checkbox="{ item }">
              <Checkbox v-model="item.isEnabled" :name="`${item.name}`" hideDetails />
            </template>
            <template #item.nameAndID="{ item }">
              <SetupParameterLabel :parameter="item" />
            </template>
            <template #item.unit="{ item }">
              {{ 'unitAbbreviation' in item ? item.unitAbbreviation : EMPTY_FIELD_SIGN }}
            </template>
          </OverviewTable>
        </v-col>
      </v-row>
    </template>
    <template #actions>
      <TextVBtn :text="$t('Cancel')" @click="emits('update:modelValue', false)" />
      <SecondaryVBtn @click="addParameters">
        {{ $t('Apply') }}
      </SecondaryVBtn>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
:deep(.v-selection-control) {
  min-height: unset;
}
</style>
